import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, onSnapshot, query, where, orderBy, limit, getDocs, doc, updateDoc, serverTimestamp, getDoc, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../utils/firebase';
import StatCard from '../components/views/StatCard';
import Tooltip from '../components/common/Tooltip';
import Toast from '../components/common/Toast';
import { Link, useNavigate } from 'react-router-dom';
import { useTableState } from '../hooks/useTableState';
import OpenTasksTable from '../components/views/OpenTasksTable';
import OpportunityPipeline from '../components/opportunities/OpportunityPipeline';
import ReEngagementQueue from '../components/opportunities/ReEngagementQueue';
import TaskModal from '../components/forms/TaskModal';
import { collectionPath } from '../utils/firestoreCollections';
import { formatDateYYYYMMDD, convertDateForSave } from '../utils/dateHelpers';
import { usePermissions } from '../hooks/usePermissions';
import { FEATURE_PERMISSIONS, getRbacMatchNames } from '../utils/permissions';
import { calculateWinProbability } from '../utils/winProbability';
import PaginationControls from '../components/common/PaginationControls';
import { getValueRangeLabel, getValueRangeMidpoint } from '../components/forms/OppModal';
import { logEntityChanges } from '../utils/logger';
import { useAuth } from '../components/auth/AuthContext';
import useEnrichedOpportunities from '../hooks/useEnrichedOpportunities';

const DashboardPage = ({ user }) => {
    const { currentUser } = useAuth();
    const { can } = usePermissions(user);
    const [opportunities, setOpportunities] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [orders, setOrders] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [salesReps, setSalesReps] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingOrders, setLoadingOrders] = useState(true);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [error, setError] = useState(null);
    
    // Direct revenue values from cache (simple and fast)
    const [ytdRevenue, setYtdRevenue] = useState(0);
    const [rolling365Revenue, setRolling365Revenue] = useState(0);
    const [loadingRolling365, setLoadingRolling365] = useState(true);
    
    // Cache refresh state
    const [cacheLastUpdated, setCacheLastUpdated] = useState(null);
    const [refreshingCache, setRefreshingCache] = useState(false);
    const [refreshMessage, setRefreshMessage] = useState('');
    
    // Opportunities (Last 180 days) Table State
    const [selectedSalesRep, setSelectedSalesRep] = useState('');
    const { sortBy, sortDir, page: oppPage, setSort: setOppSort, setPage: setOppPage } = useTableState({ sortBy: 'dueDate', sortDir: 'asc', page: 1 }, 'opp');
    const oppInitialLoadRef = useRef(false);
    const prevOppCountRef = useRef(0);
    
    // Tasks Table State
    const { sortBy: tasksSortBy, sortDir: tasksSortDir, page: tasksPage, setSort: setTasksSort, setPage: setTasksPage } = useTableState({ sortBy: 'dueDate', sortDir: 'asc', page: 1 }, 'task');
    const tasksInitialLoadRef = useRef(false);
    const prevTasksCountRef = useRef(0);
    const [editTask, setEditTask] = useState(null);
    const [showEditTaskModal, setShowEditTaskModal] = useState(false);
    
    // Toast state for error/success messages
    const [toast, setToast] = useState(null);

    const PAGE_SIZE = 10;

    const navigate = useNavigate();

    // Helper to get current user's name(s) for matching with order commissions.
    // NOTE: For repPerformance lookups, we recompute match names inline from `user`
    // to avoid state-timing races when the user prop changes (e.g. impersonation).
    // userDisplayName is retained here for log lines and the effect gate below.
    const [userDisplayName, setUserDisplayName] = useState('');
    useEffect(() => {
        if (!user || can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL)) return;
        setUserDisplayName(user.displayName || user.full_name || '');
    }, [user, can]);

    // YTD date range for Deals Won calculation
    const ytdRange = useMemo(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 1);
        return { start, end: now };
    }, []);

    // Rolling 365-day date range (364 * 24h so start lands exactly 365 days ago at midnight)
    const rolling365Range = useMemo(() => {
        const now = new Date();
        const start = new Date(now.getTime() - 364 * 24 * 60 * 60 * 1000);
        start.setHours(0, 0, 0, 0);
        return { start, end: now };
    }, []);

    // 180-day lookback for Opportunities, Pipeline, and Tasks
    const last180DaysRange = useMemo(() => {
        const now = new Date();
        const start = new Date(now.getTime() - 179 * 24 * 60 * 60 * 1000);
        start.setHours(0, 0, 0, 0);
        return { start, end: now };
    }, []);

    // Customers subscription (role-aware, fetch fewer initially for faster load)
    useEffect(() => {
        if (!user?.uid) {
            setError('User not authenticated');
            return;
        }
        let unsub = () => {};
        setLoading(true);
        try {
            if (can(FEATURE_PERMISSIONS.CUSTOMER_VIEW_ALL)) {
                unsub = onSnapshot(
                    query(
                        collection(db, collectionPath('customers')),
                        orderBy('customerName', 'asc'),
                        limit(100) // Reduced from 500 for faster initial load
                    ),
                    (snapshot) => {
                        setCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                        setError(null);
                        setLoading(false);
                    },
                    (err) => {
                        console.error('Customers fetch error:', err);
                        setError(err);
                        setLoading(false);
                    }
                );
            } else {
                // For non-admins, fetch customers where they are a sales rep
                getDocs(
                    query(
                        collection(db, collectionPath('customers')),
                        orderBy('customerName', 'asc'),
                        limit(100) // Reduced from 500
                    )
                ).then(snapshot => {
                    let allCustomers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    
                    // Filter client-side by salesReps array matching user's displayName/full_name
                    if (user.displayName || user.full_name) {
                        const matchNames = getRbacMatchNames(user);
                        const matchSet = new Set(matchNames);
                        allCustomers = allCustomers.filter(customer => {
                            if (!Array.isArray(customer.salesReps)) return false;
                            return customer.salesReps.some(rep => 
                                rep.salesRepName && matchSet.has(rep.salesRepName.toLowerCase().trim())
                            );
                        });
                    }
                    
                    setCustomers(allCustomers);
                    setError(null);
                    setLoading(false);
                }).catch(err => {
                    console.error('Customers fetch error:', err);
                    setError(err);
                    setLoading(false);
                });
            }
        } catch (err) {
            console.error('Customers fetch error:', err);
            setError(err);
            setLoading(false);
        }
        return () => { try { unsub(); } catch (e) {} };
    }, [user]);

    // Opportunities subscription (fetch fewer initially for pipeline board)
    useEffect(() => {
        if (!user?.uid) return;
        let unsubscribers = [];
        let mounted = true;
        setOpportunities([]);
        const cleanup = () => {
            unsubscribers.forEach(u => { try { u(); } catch (e) {} });
        };
        try {
            if (can(FEATURE_PERMISSIONS.OPP_VIEW_ALL)) {
                const unsub = onSnapshot(
                    query(
                        collection(db, collectionPath('opportunities')),
                        orderBy('startDate', 'desc'),
                        limit(100) // Reduced from 500 for faster load
                    ),
                    (snapshot) => {
                        if (!mounted) return;
                        setOpportunities(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                        setError(null);
                    },
                    (err) => {
                        setError(err);
                    }
                );
                unsubscribers.push(unsub);
            } else {
                // For non-admins, fetch all their opportunities
                const q1 = query(
                    collection(db, collectionPath('opportunities')),
                    where('uid', '==', user.uid),
                    orderBy('startDate', 'desc'),
                    limit(100) // Reduced from 500
                );
                const unsub1 = onSnapshot(
                    q1,
                    (snapshot) => {
                        if (!mounted) return;
                        setOpportunities(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                        setError(null);
                    },
                    (err) => {
                        setError(err);
                    }
                );
                unsubscribers.push(unsub1);
            }
        } catch (err) {
            setError(err);
        }
        return () => {
            mounted = false;
            cleanup();
        };
    }, [user, can]);

    // Tasks subscription (last 180 days, filtered by createdAt)
    useEffect(() => {
        if (!user?.uid) return;
        let unsub = () => {};
        try {
            if (can(FEATURE_PERMISSIONS.TASK_VIEW_ALL)) {
                // Admin: Get all tasks
                unsub = onSnapshot(
                    query(
                        collection(db, collectionPath('tasks')),
                        orderBy('createdAt', 'desc'),
                        limit(500)
                    ),
                    (snapshot) => {
                        setTasks(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                    },
                    (err) => {
                        console.error('Tasks fetch error:', err);
                    }
                );
            } else {
                // Non-admin: Get only their tasks
                unsub = onSnapshot(
                    query(
                        collection(db, collectionPath('tasks')),
                        where('uid', '==', user.uid),
                        orderBy('createdAt', 'desc'),
                        limit(500)
                    ),
                    (snapshot) => {
                        setTasks(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                    },
                    (err) => {
                        console.error('Tasks fetch error:', err);
                    }
                );
            }
        } catch (err) {
            console.error('Tasks subscription error:', err);
        }
        return () => { try { unsub(); } catch (e) {} };
    }, [user, can]);

    // =============================================================================
    // REVENUE DATA - Cache-first approach for BOTH admins and non-admins
    // Admins see total revenue, non-admins see their own from repPerformance
    // =============================================================================
    useEffect(() => {
        if (!user?.uid) return;
        
        const isAdmin = can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL);
        
        // Non-admins need their displayName to filter - wait for it
        if (!isAdmin && !userDisplayName) {
            // Still loading user data, keep loading state
            return;
        }
        
        let aborted = false;
        
        // Fetch from Firestore reportsCache (fast - single doc read)
        const fetchRevenue = async () => {
            setLoadingOrders(true);
            
            try {
                const cacheDoc = await getDoc(doc(db, collectionPath('reportsCache'), 'summary_ytd'));
                
                if (aborted) return;
                
                if (cacheDoc.exists()) {
                    const data = cacheDoc.data();
                    const cacheAge = data.lastUpdated?.toDate?.() || new Date(0);
                    
                    let revenueValue = 0;
                    
                    if (isAdmin) {
                        // Admin: Use total revenue from summary
                        revenueValue = data.summary?.totalRevenue || 0;
                    } else {
                        // Non-admin: Find their revenue in repPerformance array.
                        // Recompute match names from the current user prop (rather than relying on
                        // userMatchNames state) so an impersonation/user switch cannot leave a stale
                        // empty set in this async closure. Sum across all matching entries because
                        // AM commissions_displayNames casing can differ from users.displayName
                        // (e.g. "Swinging it" from orders vs "Swinging It" from opportunities).
                        const matchSet = new Set(getRbacMatchNames(user));
                        const matches = (data.repPerformance || []).filter(
                            rep => matchSet.has(rep.displayName?.toLowerCase().trim()) ||
                                   matchSet.has(rep.name?.toLowerCase().trim())
                        );
                        revenueValue = matches.reduce(
                            (sum, r) => sum + (r.revenue || r.totalOrdered || 0),
                            0
                        );
                    }
                    
                    if (aborted) return;
                    setYtdRevenue(revenueValue);
                    setCacheLastUpdated(cacheAge);
                    setLoadingOrders(false);
                    return;
                }
                
                // Cache miss - show 0
                console.warn('[Dashboard] YTD cache not found');
                if (aborted) return;
                setYtdRevenue(0);
                setLoadingOrders(false);
                
            } catch (err) {
                if (aborted) return;
                console.error('Revenue fetch error:', err);
                setYtdRevenue(0);
                setLoadingOrders(false);
            }
        };
        
        fetchRevenue();
        
        return () => {
            aborted = true;
        };
    }, [user?.uid, userDisplayName, can]);

    // Rolling 365-day revenue - Uses dedicated 365d cache for accurate data
    useEffect(() => {
        if (!user?.uid) return;
        
        const isAdmin = can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL);
        
        // Non-admins need their displayName to filter - wait for it
        if (!isAdmin && !userDisplayName) {
            return;
        }
        
        let aborted = false;
        
        const fetchRolling365 = async () => {
            setLoadingRolling365(true);
            
            try {
                // Use dedicated 365d cache
                const cacheDoc = await getDoc(doc(db, collectionPath('reportsCache'), 'summary_365d'));
                
                if (aborted) return;
                
                if (cacheDoc.exists()) {
                    const data = cacheDoc.data();
                    
                    let revenueValue = 0;
                    
                    if (isAdmin) {
                        // Admin: Use total revenue from summary
                        revenueValue = data.summary?.totalRevenue || 0;
                    } else {
                        // Non-admin: Find revenue across all matching repPerformance entries.
                        // Recompute match names from the current user prop and sum matches
                        // (see YTD fetch above for the same rationale).
                        const matchSet = new Set(getRbacMatchNames(user));
                        const matches = (data.repPerformance || []).filter(
                            rep => matchSet.has(rep.displayName?.toLowerCase().trim()) ||
                                   matchSet.has(rep.name?.toLowerCase().trim())
                        );
                        revenueValue = matches.reduce(
                            (sum, r) => sum + (r.revenue || r.totalOrdered || 0),
                            0
                        );
                    }
                    
                    if (aborted) return;
                    setRolling365Revenue(revenueValue);
                    setLoadingRolling365(false);
                    return;
                }
                
                // Cache miss - try 180d as fallback with estimate
                console.warn('[Dashboard] 365d cache not found, trying 180d fallback...');
                const fallbackDoc = await getDoc(doc(db, collectionPath('reportsCache'), 'summary_180d'));
                
                if (aborted) return;
                
                if (fallbackDoc.exists()) {
                    const data = fallbackDoc.data();
                    let revenueValue = 0;
                    
                    if (isAdmin) {
                        revenueValue = (data.summary?.totalRevenue || 0) * 2;
                    } else {
                        const matchSet = new Set(getRbacMatchNames(user));
                        const matches = (data.repPerformance || []).filter(
                            rep => matchSet.has(rep.displayName?.toLowerCase().trim()) ||
                                   matchSet.has(rep.name?.toLowerCase().trim())
                        );
                        const sum = matches.reduce(
                            (s, r) => s + (r.revenue || r.totalOrdered || 0),
                            0
                        );
                        revenueValue = sum * 2;
                    }
                    
                    console.warn('[Dashboard] ⚠️ Using 180d x2 ESTIMATE for 365d:', revenueValue);
                    
                    if (aborted) return;
                    setRolling365Revenue(revenueValue);
                    setLoadingRolling365(false);
                    return;
                }
                
                // No cache available
                console.warn('[Dashboard] No cache available for 365d revenue');
                if (aborted) return;
                setRolling365Revenue(0);
                setLoadingRolling365(false);
                
            } catch (err) {
                if (aborted) return;
                console.error('Rolling 365 revenue fetch error:', err);
                setRolling365Revenue(0);
                setLoadingRolling365(false);
            }
        };
        
        fetchRolling365();
        
        return () => {
            aborted = true;
        };
    }, [user?.uid, userDisplayName, can]);

    // Users and salesReps - LAZY LOADED (deferred, not needed immediately)
    // Load users for display names in Open Tasks table
    useEffect(() => {
        if (!user?.uid) {
            setUsers([]);
            setSalesReps([]);
            return;
        }
        
        const isAdmin = can(FEATURE_PERMISSIONS.USER_VIEW_ALL);
        
        // Defer users fetch by 800ms to prioritize critical data
        const timer = setTimeout(() => {
            setLoadingUsers(true);
            
            (async () => {
                try {
                    const usersSnap = await getDocs(
                        query(collection(db, collectionPath('users')), orderBy('displayName', 'asc'), limit(50))
                    );
                    const usersList = usersSnap.docs.map(doc => ({ ...doc.data(), uid: doc.id }));
                    setUsers(usersList);
                    
                    // Load sales reps (admins only)
                    if (isAdmin) {
                        const salesSnap = await getDocs(
                            query(collection(db, collectionPath('users')), where('role', '==', 'salesRep'), orderBy('displayName', 'asc'), limit(50))
                        );
                        setSalesReps(salesSnap.docs.map(doc => ({ ...doc.data(), uid: doc.id })));
                    }
                    
                    setLoadingUsers(false);
                } catch (err) {
                    console.error('Error loading users:', err);
                    setLoadingUsers(false);
                }
            })();
        }, 800); // 800ms delay
        
        return () => clearTimeout(timer);
    }, [user, can]);

    // Stats calculation - show all user data (Win Rate uses YTD)
    const stats = useMemo(() => {
        // Calculate YTD closed opportunities (won + lost)
        const ytdClosedOpportunities = opportunities.filter(opp => {
            const stage = (opp.stage || '').trim().toLowerCase();
            if (stage !== 'closed won' && stage !== 'closed lost') return false;
            // Use closedDate if available, otherwise fall back to startDate
            const dateToCheck = opp.closedDate || opp.startDate;
            if (!dateToCheck) return false;
            const d = (typeof dateToCheck.toDate === 'function') ? dateToCheck.toDate() : new Date(dateToCheck);
            if (isNaN(d)) return false;
            if (d < ytdRange.start || d > ytdRange.end) return false;
            return true;
        });
        
        const ytdClosedWon = ytdClosedOpportunities.filter(opp => {
            const stage = (opp.stage || '').trim().toLowerCase();
            return stage === 'closed won';
        }).length;
        
        const ytdClosedLost = ytdClosedOpportunities.filter(opp => {
            const stage = (opp.stage || '').trim().toLowerCase();
            return stage === 'closed lost';
        }).length;
        
        const ytdTotalClosed = ytdClosedWon + ytdClosedLost;
        const winRate = ytdTotalClosed > 0 ? Math.round((ytdClosedWon / ytdTotalClosed) * 100) : 0;
        const winRateFormula = ytdTotalClosed > 0 ? `${ytdClosedWon} Won / ${ytdTotalClosed} Total (${ytdClosedWon} Won + ${ytdClosedLost} Lost)` : 'No closed deals yet';
        
        return {
            winRate: winRate,
            winRateFormula: winRateFormula,
            revenueForecast: opportunities
                .filter(opp => {
                    // Only count open opportunities (all, no date filter)
                    if (["Closed Won", "Closed Lost"].includes(opp.stage)) return false;
                    return true;
                })
                .reduce((sum, opp) => sum + getValueRangeMidpoint(opp.value), 0),
            openOpportunities: opportunities.filter(opp => {
                if (["Closed Won", "Closed Lost"].includes(opp.stage)) return false;
                return true;
            }).length,
            newLeads: opportunities.filter(opp => {
                // Normalize stage for robust comparison
                const stage = (opp.stage || '').trim().toLowerCase();
                if (stage !== 'new') return false;
                // Assignment filter for non-admins
                if (
                    !can(FEATURE_PERMISSIONS.OPP_VIEW_ALL) &&
                    String(opp.uid) !== String(user?.uid) &&
                    !customers.some(c => c.id === opp.customerId && c.uid === user.uid)
                ) {
                    return false;
                }
                return true;
            }).length,
            // Use direct revenue values from cache (no filtering needed)
            totalRevenueYTD: ytdRevenue,
            totalRevenueRolling365: rolling365Revenue
        };
    }, [opportunities, ytdRevenue, rolling365Revenue, ytdRange, user, customers, can]);

    // Memoized user lookup (used by table sorts, ReEngagementQueue, etc.)
    const usersById = useMemo(() => {
        const m = new Map();
        users.forEach(u => m.set(u.id || u.uid, u));
        return m;
    }, [users]);

    // Enrich opportunities for the pipeline board via shared hook
    // (Includes ALL opportunities — PipelineBoard handles per-stage display)
    const opportunitiesWithNames = useEnrichedOpportunities(opportunities, { customers, users });

    // Last 180 Days Opportunities filtering and sorting
    const filteredOpportunities = useMemo(() => {
        return opportunities.filter(opp => {
            // Always include follow-up-flagged opportunities regardless of date
            const isFlagged = !!opp.followUpFlag;

            if (!isFlagged) {
                // Filter by last 180 days startDate
                const startDate = opp.startDate ? 
                    (typeof opp.startDate.toDate === 'function' ? opp.startDate.toDate() : new Date(opp.startDate)) : null;
                if (!startDate || isNaN(startDate)) return false;
                if (startDate < last180DaysRange.start || startDate > last180DaysRange.end) return false;
            }
            
            // Sales rep filter (admin only)
            if (can(FEATURE_PERMISSIONS.OPP_VIEW_ALL)) {
                if (selectedSalesRep && String(opp.uid) !== String(selectedSalesRep)) return false;
            }
            
            return true;
        });
    }, [opportunities, last180DaysRange, selectedSalesRep, can]);
    
    const sortedOpportunities = useMemo(() => {
        const sorted = [...filteredOpportunities].sort((a, b) => {
            let aValue = a[sortBy];
            let bValue = b[sortBy];
            
            // Handle dates
            if (sortBy === 'startDate' || sortBy === 'dueDate') {
                aValue = aValue ? (typeof aValue.toDate === 'function' ? aValue.toDate() : new Date(aValue)) : new Date(0);
                bValue = bValue ? (typeof bValue.toDate === 'function' ? bValue.toDate() : new Date(bValue)) : new Date(0);
            }
            
            // Handle numbers
            if (sortBy === 'value') {
                aValue = Number(aValue) || 0;
                bValue = Number(bValue) || 0;
            }
            
            // Handle assigned user
            if (sortBy === 'assignedUser') {
                const userA = usersById.get(a.uid);
                const userB = usersById.get(b.uid);
                aValue = userA?.displayName || userA?.email || 'Unassigned';
                bValue = userB?.displayName || userB?.email || 'Unassigned';
            }
            
            if (typeof aValue === 'string' || typeof bValue === 'string') {
                return sortDir === 'asc'
                    ? (aValue?.toString() || '').localeCompare(bValue?.toString() || '')
                    : (bValue?.toString() || '').localeCompare(aValue?.toString() || '');
            }
            return sortDir === 'asc' ? (aValue > bValue ? 1 : -1) : (bValue > aValue ? 1 : -1);
        });
        return sorted;
    }, [filteredOpportunities, sortBy, sortDir, usersById]);
    
    const oppTotalPages = Math.max(1, Math.ceil(sortedOpportunities.length / PAGE_SIZE));
    const paginatedOpportunities = useMemo(() => {
        const startIdx = (oppPage - 1) * PAGE_SIZE;
        return sortedOpportunities.slice(startIdx, startIdx + PAGE_SIZE);
    }, [sortedOpportunities, oppPage]);
    
    const handleSort = (field) => {
        const newDir = sortBy === field ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
        setOppSort(field, newDir);
    };
    
    useEffect(() => {
        // Always reset on sales rep filter change
        if (selectedSalesRep) { setOppPage(1); return; }
        // Skip initial data load
        if (!oppInitialLoadRef.current) {
            if (filteredOpportunities.length > 0) {
                oppInitialLoadRef.current = true;
                prevOppCountRef.current = filteredOpportunities.length;
            }
            return;
        }
        if (filteredOpportunities.length !== prevOppCountRef.current) {
            setOppPage(1);
        }
        prevOppCountRef.current = filteredOpportunities.length;
    }, [selectedSalesRep, filteredOpportunities.length, setOppPage]);

    // Helper function to get user name from uid
    const getUserName = useCallback((userId) => {
        const u = usersById.get(userId);
        if (!u) return userId || '-';
        if (u.firstName || u.lastName) {
            return `${u.firstName || ''} ${u.lastName || ''}`.trim();
        }
        return u.displayName || u.name || u.email || u.uid || '-';
    }, [usersById]);

    // Helper function to check if date is past or today
    const isPastOrToday = useCallback((dateStrOrObj) => {
        let date;
        if (!dateStrOrObj) return false;
        if (typeof dateStrOrObj === 'string') {
            date = new Date(dateStrOrObj + 'T00:00:00');
        } else if (dateStrOrObj.seconds) {
            date = new Date(dateStrOrObj.seconds * 1000);
        } else if (typeof dateStrOrObj.toDate === 'function') {
            date = dateStrOrObj.toDate();
        } else {
            date = new Date(dateStrOrObj);
        }
        const today = new Date();
        today.setHours(0,0,0,0);
        date.setHours(0,0,0,0);
        return date <= today;
    }, []);

    // Format date helper
    const formatTaskDate = useCallback((dateObj) => {
        if (!dateObj) return '-';
        let date;
        if (typeof dateObj.toDate === 'function') {
            date = dateObj.toDate();
        } else if (dateObj.seconds) {
            date = new Date(dateObj.seconds * 1000);
        } else {
            date = new Date(dateObj);
        }
        return isNaN(date) ? '-' : date.toLocaleDateString();
    }, []);

    // Last 180 Days Open Tasks filtering
    const filteredTasks = useMemo(() => {
        return tasks.filter(task => {
            // Only show open tasks
            const status = (task.status || '').toLowerCase();
            if (status !== 'open') return false;
            
            // Filter by last 180 days createdAt
            const createdAt = task.createdAt ? 
                (typeof task.createdAt.toDate === 'function' ? task.createdAt.toDate() : 
                 task.createdAt.seconds ? new Date(task.createdAt.seconds * 1000) : new Date(task.createdAt)) : null;
            if (!createdAt || isNaN(createdAt)) return false;
            if (createdAt < last180DaysRange.start || createdAt > last180DaysRange.end) return false;
            
            return true;
        });
    }, [tasks, last180DaysRange]);
    
    // Sort tasks
    const sortedTasks = useMemo(() => {
        const sorted = [...filteredTasks].sort((a, b) => {
            let valA, valB;
            switch (tasksSortBy) {
                case 'taskNumber':
                    valA = a.taskNumber || 0;
                    valB = b.taskNumber || 0;
                    break;
                case 'title':
                    valA = (a.title || '').toLowerCase();
                    valB = (b.title || '').toLowerCase();
                    break;
                case 'dueDate':
                    valA = a.dueDate?.seconds || 0;
                    valB = b.dueDate?.seconds || 0;
                    break;
                case 'assignedTo':
                    valA = (getUserName(a.uid) || '').toLowerCase();
                    valB = (getUserName(b.uid) || '').toLowerCase();
                    break;
                case 'updatedAt':
                    valA = a.updatedAt?.seconds || 0;
                    valB = b.updatedAt?.seconds || 0;
                    break;
                case 'createdAt':
                    valA = a.createdAt?.seconds || 0;
                    valB = b.createdAt?.seconds || 0;
                    break;
                default:
                    valA = '';
                    valB = '';
            }
            if (valA < valB) return tasksSortDir === 'asc' ? -1 : 1;
            if (valA > valB) return tasksSortDir === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [filteredTasks, tasksSortBy, tasksSortDir, getUserName]);
    
    // Paginate tasks
    const tasksTotalPages = Math.max(1, Math.ceil(sortedTasks.length / PAGE_SIZE));
    const paginatedTasks = useMemo(() => {
        const startIdx = (tasksPage - 1) * PAGE_SIZE;
        return sortedTasks.slice(startIdx, startIdx + PAGE_SIZE);
    }, [sortedTasks, tasksPage]);
    
    // Handle tasks sorting
    const handleTasksSort = (field) => {
        const newDir = tasksSortBy === field ? (tasksSortDir === 'asc' ? 'desc' : 'asc') : 'asc';
        setTasksSort(field, newDir);
    };
    
    // Reset tasks page when filters change (skip initial data load)
    useEffect(() => {
        if (!tasksInitialLoadRef.current) {
            if (filteredTasks.length > 0) {
                tasksInitialLoadRef.current = true;
                prevTasksCountRef.current = filteredTasks.length;
            }
            return;
        }
        if (filteredTasks.length !== prevTasksCountRef.current) {
            setTasksPage(1);
        }
        prevTasksCountRef.current = filteredTasks.length;
    }, [filteredTasks.length, setTasksPage]);
    
    // Fetch cache last updated time from YTD cache (runs once on mount)
    useEffect(() => {
        const fetchCacheTime = async () => {
            try {
                const cacheDoc = await getDoc(doc(db, collectionPath('reportsCache'), 'summary_ytd'));
                if (cacheDoc.exists()) {
                    const lastUpdated = cacheDoc.data().lastUpdated?.toDate?.();
                    if (lastUpdated) {
                        setCacheLastUpdated(lastUpdated);
                    }
                }
            } catch (err) {
                console.error('Error fetching cache time:', err);
            }
        };
        fetchCacheTime();
    }, []); // Run once on mount
    
    // Manual cache refresh handler (admin only)
    const handleRefreshCache = async () => {
        if (!can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL)) {
            setRefreshMessage('Only admins can refresh the cache');
            setTimeout(() => setRefreshMessage(''), 3000);
            return;
        }
        
        setRefreshingCache(true);
        setRefreshMessage('');
        
        try {
            const functions = getFunctions();
            // Increase timeout to 540s (9 minutes) to match Cloud Function timeout
            const refreshReportsCache = httpsCallable(functions, 'refreshReportsCache', { timeout: 540000 });
            const result = await refreshReportsCache();
            
            // Update cache time
            setCacheLastUpdated(new Date());
            setRefreshMessage(result.data?.message || 'Cache refreshed successfully');
            
            // Directly re-fetch the updated cache values (admin only sees this button)
            setLoadingOrders(true);
            setLoadingRolling365(true);
            
            // Fetch YTD (admin gets total)
            const ytdDoc = await getDoc(doc(db, collectionPath('reportsCache'), 'summary_ytd'));
            if (ytdDoc.exists()) {
                setYtdRevenue(ytdDoc.data().summary?.totalRevenue || 0);
                setCacheLastUpdated(ytdDoc.data().lastUpdated?.toDate?.() || new Date());
            }
            setLoadingOrders(false);
            
            // Fetch 365d (admin gets total)
            const r365Doc = await getDoc(doc(db, collectionPath('reportsCache'), 'summary_365d'));
            if (r365Doc.exists()) {
                setRolling365Revenue(r365Doc.data().summary?.totalRevenue || 0);
            }
            setLoadingRolling365(false);
            
        } catch (err) {
            console.error('Cache refresh error:', err);
            setRefreshMessage(err.message || 'Failed to refresh cache');
            setLoadingOrders(false);
            setLoadingRolling365(false);
        } finally {
            setRefreshingCache(false);
            setTimeout(() => setRefreshMessage(''), 5000);
        }
    };
    
    // Format cache time for display
    const formatCacheTime = (date) => {
        if (!date) return 'Unknown';
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        
        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} min ago`;
        if (diffHours < 24) return `${diffHours} hr ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    };

    if (loading) {
        return (
            <main className="flex-grow p-6 overflow-y-auto bg-white">
                <div className="flex items-center justify-center h-64">
                    <p className="text-gray-500 dark:text-gray-400">Loading dashboard...</p>
                </div>
            </main>
        );
    }

    return (
        <main className="flex-grow p-6 overflow-y-auto bg-white relative">
            {/* Cache Refresh Loading Overlay */}
            {refreshingCache && (
                <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-4">
                        <div className="flex flex-col items-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-2">Refreshing Revenue Cache</h3>
                            <p className="text-sm text-gray-600 text-center">
                                This may take 2-5 minutes. Please wait...
                            </p>
                            <p className="text-xs text-gray-400 mt-2">
                                Computing YTD and 365-day revenue data
                            </p>
                        </div>
                    </div>
                </div>
            )}
            
            <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <h2 className="text-2xl font-bold text-black-900">Dashboard</h2>
                        <Tooltip text={
                            <div className="text-left">
                                <div className="font-semibold mb-2">Dashboard Overview</div>
                                <div className="text-sm space-y-1">
                                    <div><strong>Dashboard Cards:</strong> New Leads, All Open Opportunities, Deals Won YTD, Revenue Forecast from Opportunities, Total Revenue (YTD), and Rolling 365 Days Revenue.</div>
                                    <div><strong>Open Tasks:</strong> All open tasks</div>
                                    <div><strong>Opportunities Pipeline:</strong> All opportunities including Closed Won/Lost (YTD)</div>
                                    <div><strong>Opportunities:</strong> All opportunities including Closed Won/Lost (Last 180 days)</div>
                                </div>
                            </div>
                        } maxWidth={350}>
                            <svg className="w-5 h-5 text-gray-500 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </Tooltip>
                    </div>
                    
                    {/* Cache Refresh Button (Admin Only) */}
                    {can(FEATURE_PERMISSIONS.ORDER_VIEW_ALL) && (
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-gray-500">
                                Revenue Cache: {formatCacheTime(cacheLastUpdated)}
                            </span>
                            <Tooltip text="Refresh the revenue cache used by YTD and 365-day revenue cards. This may take 2-5 minutes." position="left" maxWidth={280}>
                                <button
                                    onClick={handleRefreshCache}
                                    disabled={refreshingCache}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                                        refreshingCache
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                            : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                    }`}
                                >
                                    <svg 
                                        className={`w-4 h-4 ${refreshingCache ? 'animate-spin' : ''}`} 
                                        fill="none" 
                                        stroke="currentColor" 
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    {refreshingCache ? 'Refreshing...' : 'Refresh'}
                                </button>
                            </Tooltip>
                            {refreshMessage && (
                                <span className={`text-xs ${refreshMessage.includes('Failed') || refreshMessage.includes('Only') ? 'text-red-600' : 'text-green-600'}`}>
                                    {refreshMessage}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <p className="text-gray-600 text-sm">
                    Quick overview of your sales activities and performance metrics.
                </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
                <StatCard
                    title="New Leads"
                    value={stats.newLeads}
                    icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" /></svg>}
                />
                <StatCard
                    title="Open Opportunities"
                    value={stats.openOpportunities}
                    icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
                />
                <StatCard
                    title="Win Rate % (YTD)"
                    value={`${stats.winRate}%`}
                    icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                    subtitle={stats.winRateFormula}
                />
                <StatCard
                    title="Revenue Forecast"
                    value={`$${stats.revenueForecast.toLocaleString()}`}
                    icon={
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <text
                                x="12"
                                y="18"
                                textAnchor="middle"
                                fontSize="20"
                                fontWeight="bold"
                                fill="currentColor"
                                stroke="none"
                                dy="0"
                            >
                                $
                            </text>
                        </svg>
                    }
                />
                <StatCard
                    title="Total Revenue (YTD)"
                    value={loadingOrders ? 'Loading...' : `$${stats.totalRevenueYTD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    icon={
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    }
                />
                <StatCard
                    title="Revenue (365 Days)"
                    value={loadingRolling365 ? 'Loading...' : `$${stats.totalRevenueRolling365.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                    icon={
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    }
                />
            </div>

            {/* Pipeline Board - hidden on mobile */}
            <div className="mb-8 hidden md:block">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold">Opportunity Pipeline</h2>
                    <Link to="/opportunities" className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
                        View All Opportunities →
                    </Link>
                </div>
                <OpportunityPipeline
                    opportunities={opportunitiesWithNames}
                    onOpportunityClick={(opp) => navigate(`/opportunity/${opp.id}`)}
                    user={currentUser}
                    setToast={setToast}
                />
            </div>
            
            {/* Open Tasks Table (Last 180 Days) */}
            <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                        <h2 className="text-2xl font-bold">
                            Open Tasks
                            <span className="text-lg text-gray-500 font-normal ml-2">(Last 180 days)</span>
                        </h2>
                        <Tooltip text="Open tasks created in the last 180 days, filtered by Created Date" maxWidth={300}>
                            <svg className="w-5 h-5 text-gray-500 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </Tooltip>
                    </div>
                    <Link to="/tasks" className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
                        View All Tasks →
                    </Link>
                </div>
                
                <div className="bg-white rounded-lg shadow">
                    <div className="overflow-x-auto">
                        <table className="w-full whitespace-no-wrap">
                            <thead>
                                <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleTasksSort('taskNumber')}>
                                        Task # {tasksSortBy === 'taskNumber' && (tasksSortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleTasksSort('title')}>
                                        Title {tasksSortBy === 'title' && (tasksSortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3">Description</th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleTasksSort('dueDate')}>
                                        Due Date {tasksSortBy === 'dueDate' && (tasksSortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleTasksSort('assignedTo')}>
                                        Assigned To {tasksSortBy === 'assignedTo' && (tasksSortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleTasksSort('priority')}>
                                        Priority {tasksSortBy === 'priority' && (tasksSortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleTasksSort('updatedAt')}>
                                        Last Updated {tasksSortBy === 'updatedAt' && (tasksSortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleTasksSort('createdAt')}>
                                        Created Date {tasksSortBy === 'createdAt' && (tasksSortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y">
                                {paginatedTasks.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="text-center py-4 text-gray-500">
                                            No open tasks found in the last 180 days.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedTasks.map(task => (
                                        <tr key={task.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3">
                                                <Link to={`/tasks/${task.id}`} className="text-indigo-600 hover:underline font-mono">
                                                    {task.taskNumberLabel || task.taskNumber || task.id}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-3 max-w-xs truncate font-bold" title={task.title}>
                                                {task.title && task.title.length > 50
                                                    ? `${task.title.slice(0, 50)}...`
                                                    : task.title}
                                            </td>
                                            <td className="px-4 py-3 max-w-[40ch] truncate text-sm" title={task.description}>
                                                {task.description && task.description.length > 60
                                                    ? `${task.description.slice(0, 60)}...`
                                                    : task.description}
                                            </td>
                                            <td className={`px-4 py-3 text-sm whitespace-nowrap ${
                                                isPastOrToday(task.dueDate) ? 'font-bold text-red-600' : ''
                                            }`}>
                                                {formatTaskDate(task.dueDate)}
                                            </td>
                                            <td className="px-4 py-3 text-sm">{getUserName(task.uid)}</td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                    task.priority === 'High' 
                                                        ? 'bg-red-100 text-red-800'
                                                        : task.priority === 'Low'
                                                            ? 'bg-gray-100 text-gray-800'
                                                            : 'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                    {task.priority || 'Medium'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                                                {formatTaskDate(task.updatedAt)}
                                            </td>
                                            <td className="px-4 py-3 text-sm whitespace-nowrap">
                                                {formatTaskDate(task.createdAt)}
                                            </td>
                                            <td className="px-4 py-3">
                                                {can(FEATURE_PERMISSIONS.TASK_EDIT) && (
                                                <button
                                                    onClick={() => {
                                                        setEditTask(task);
                                                        setShowEditTaskModal(true);
                                                    }}
                                                    className="text-indigo-600 hover:text-indigo-900"
                                                >
                                                    Edit
                                                </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Pagination */}
                    <PaginationControls currentPage={tasksPage} totalPages={tasksTotalPages} onPageChange={setTasksPage} totalItems={sortedTasks.length} pageSize={PAGE_SIZE} itemLabel="tasks" />
                </div>
            </div>

            {/* Task Edit Modal */}
            {showEditTaskModal && editTask && (
                <TaskModal
                    salesReps={users.filter(u => u.uid)}
                    initial={editTask}
                    isAdmin={can(FEATURE_PERMISSIONS.TASK_VIEW_ALL)}
                    customers={customers}
                    opportunities={opportunities}
                    orders={orders}
                    contacts={[]}
                    onLoadCustomers={() => {}}
                    onLoadOpportunities={() => {}}
                    onLoadOrders={() => {}}
                    onLoadContacts={() => {}}
                    onSave={async (taskData) => {
                        try {
                            // Convert dueDate: if it's a Date object, convert to Timestamp; if string, parse then convert
                            let dueDateTimestamp;
                            if (taskData.dueDate instanceof Date) {
                                dueDateTimestamp = Timestamp.fromDate(taskData.dueDate);
                            } else if (typeof taskData.dueDate === 'string') {
                                dueDateTimestamp = convertDateForSave(taskData.dueDate);
                            } else if (taskData.dueDate?.toDate) {
                                // Already a Timestamp
                                dueDateTimestamp = taskData.dueDate;
                            }
                            
                            await updateDoc(doc(db, collectionPath('tasks'), editTask.id), {
                                ...taskData,
                                status: (taskData.status || 'open').toLowerCase(),
                                dueDate: dueDateTimestamp,
                                updatedAt: serverTimestamp(),
                            });
                            // Log the update
                            logEntityChanges({
                                oldObj: editTask,
                                newObj: { ...editTask, ...taskData, dueDate: dueDateTimestamp },
                                fields: ['title', 'status', 'priority', 'uid', 'dueDate', 'customerId', 'opportunityId', 'description'],
                                entityType: 'Task',
                                entityId: editTask.id,
                                user: currentUser,
                                action: 'Updated',
                            }).catch(e => console.warn('Failed to log task update:', e));
                            setShowEditTaskModal(false);
                            setEditTask(null);
                        } catch (error) {
                            console.error('Error updating task:', error);
                            alert('Failed to update task: ' + error.message);
                        }
                    }}
                    onClose={() => {
                        setShowEditTaskModal(false);
                        setEditTask(null);
                    }}
                />
            )}

            {/* Last 180 Days Opportunities Table */}
            <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                        <h2 className="text-2xl font-bold">
                            Opportunities
                            <span className="text-lg text-gray-500 font-normal ml-2">(Last 180 days)</span>
                        </h2>
                        <Tooltip text="Opportunities created in the last 180 days, filtered by Start Date" maxWidth={300}>
                            <svg className="w-5 h-5 text-gray-500 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </Tooltip>
                    </div>
                    <div className="flex items-center gap-4">
                        {can(FEATURE_PERMISSIONS.OPP_VIEW_ALL) && (
                            <div>
                                <label className="text-sm text-gray-600 mr-2">Sales Rep:</label>
                                <select
                                    value={selectedSalesRep}
                                    onChange={(e) => setSelectedSalesRep(e.target.value)}
                                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                >
                                    <option value="">All Sales Reps</option>
                                    {users.map(user => (
                                        <option key={user.id || user.uid} value={user.uid}>
                                            {user.displayName || user.email || 'Unknown'}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <Link to="/opportunities" className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
                            View All Opportunities →
                        </Link>
                    </div>
                </div>
                
                <div className="bg-white rounded-lg shadow">
                    <div className="overflow-x-auto">
                        <table className="w-full whitespace-no-wrap">
                            <thead>
                                <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                                    <th className="px-4 py-3 cursor-pointer whitespace-nowrap" onClick={() => handleSort('opportunityNumber')}>
                                        Opportunity # {sortBy === 'opportunityNumber' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('stage')}>
                                        Stage {sortBy === 'stage' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('customerName')}>
                                        Customer {sortBy === 'customerName' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('contactName')}>
                                        Contact Name {sortBy === 'contactName' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('contactEmail')}>
                                        Contact Email {sortBy === 'contactEmail' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('leadSource')}>
                                        Lead Source {sortBy === 'leadSource' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('value')}>
                                        Value {sortBy === 'value' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('startDate')}>
                                        Start Date {sortBy === 'startDate' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('dueDate')}>
                                        Due Date {sortBy === 'dueDate' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('assignedUser')}>
                                        Sales Rep {sortBy === 'assignedUser' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3 cursor-pointer" onClick={() => handleSort('winProbability')}>
                                        Win Probability (%) {sortBy === 'winProbability' && (sortDir === 'asc' ? '▲' : '▼')}
                                    </th>
                                    <th className="px-4 py-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y">
                                {paginatedOpportunities.length === 0 ? (
                                    <tr>
                                        <td colSpan="12" className="text-center py-4 text-gray-500">
                                            No opportunities found for this year.
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedOpportunities.map(opp => {
                                        const assignedUser = usersById.get(opp.uid);
                                        const startDateStr = opp.startDate
                                            ? (typeof opp.startDate.toDate === 'function'
                                                ? opp.startDate.toDate().toLocaleDateString()
                                                : new Date(opp.startDate).toLocaleDateString())
                                            : '';
                                        const dueDateStr = opp.dueDate
                                            ? (typeof opp.dueDate.toDate === 'function'
                                                ? opp.dueDate.toDate().toLocaleDateString()
                                                : new Date(opp.dueDate).toLocaleDateString())
                                            : '';
                                        
                                        // Determine row color based on stage
                                        let stageColor = '';
                                        const stage = (opp.stage || '').toLowerCase();
                                        if (stage === 'closed won') stageColor = 'bg-green-100';
                                        else if (stage === 'closed lost') stageColor = 'bg-red-50';
                                        else if (stage === 'new') stageColor = 'bg-teal-100';
                                        else if (stage === 'qualified') stageColor = 'bg-indigo-50';
                                        else if (stage === 'proposal sent') stageColor = 'bg-purple-50';
                                        else if (stage === 'not qualified') stageColor = 'bg-gray-100';
                                        
                                        return (
                                            <tr key={opp.id} className={`hover:bg-gray-50 ${stageColor}`}>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <Link to={`/opportunity/${opp.id}`} className="text-indigo-600 hover:underline">
                                                        {opp.opportunityNumber || opp.number || opp.id}
                                                    </Link>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                        stage === 'closed won' ? 'bg-green-100 text-green-800' :
                                                        stage === 'closed lost' ? 'bg-red-100 text-red-700' :
                                                        stage === 'new' ? 'bg-teal-100 text-teal-700' :
                                                        stage === 'qualified' ? 'bg-indigo-100 text-indigo-700' :
                                                        stage === 'proposal sent' ? 'bg-purple-100 text-purple-700' :
                                                        stage === 'not qualified' ? 'bg-gray-100 text-gray-600' :
                                                        'bg-gray-100 text-gray-700'
                                                    }`}>
                                                        {opp.stage || 'Unknown'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 max-w-[200px] truncate" title={opp.customerName}>
                                                    {opp.customerName || 'Unknown'}
                                                </td>
                                                <td className="px-4 py-3">{opp.contactName || '-'}</td>
                                                <td className="px-4 py-3 max-w-[200px] truncate" title={opp.contactEmail}>
                                                    {opp.contactEmail || '-'}
                                                </td>
                                                <td className="px-4 py-3">{opp.leadSource || '-'}</td>
                                                <td className="px-4 py-3">
                                                    {getValueRangeLabel(opp.value)}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">{startDateStr}</td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={(() => {
                                                        if (!opp.dueDate || stage === 'closed won' || stage === 'closed lost') return '';
                                                        try {
                                                            let dueDate;
                                                            if (typeof opp.dueDate.toDate === 'function') {
                                                                dueDate = opp.dueDate.toDate();
                                                            } else if (typeof opp.dueDate === 'string') {
                                                                // Handle string dates like "11/26/2025"
                                                                dueDate = new Date(opp.dueDate);
                                                            } else {
                                                                dueDate = new Date(opp.dueDate);
                                                            }
                                                            
                                                            if (isNaN(dueDate.getTime())) return '';
                                                            
                                                            const today = new Date();
                                                            dueDate.setHours(0,0,0,0);
                                                            today.setHours(0,0,0,0);
                                                            
                                                            return dueDate <= today ? 'text-red-600 font-bold' : '';
                                                        } catch (e) {
                                                            console.error('Date parsing error:', e, opp.dueDate);
                                                            return '';
                                                        }
                                                    })()}>
                                                        {dueDateStr || '-'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {assignedUser ? (assignedUser.displayName || assignedUser.email || 'Unknown') : 'Unassigned'}
                                                </td>
                                                <td className="px-4 py-3 text-center font-semibold">
                                                    {calculateWinProbability(opp.stage, opp.engagement)}%
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => navigate(`/opportunity/${opp.id}`)}
                                                            className="text-indigo-600 hover:text-indigo-900"
                                                        >
                                                            {can(FEATURE_PERMISSIONS.OPP_EDIT) ? 'Edit' : 'View'}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* Pagination */}
                    <PaginationControls currentPage={oppPage} totalPages={oppTotalPages} onPageChange={setOppPage} totalItems={sortedOpportunities.length} pageSize={PAGE_SIZE} itemLabel="opportunities" />
                </div>
            </div>

            <ReEngagementQueue usersById={usersById} className="mb-8" />
            
            {/* Toast notifications */}
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
        </main>
    );
};

export default DashboardPage;

